/**
 * Admin Billing Dashboard
 * Epic 86: Admin Dashboard
 *
 * Platform admin view for managing all customer subscriptions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tile, DataTable, Button, Search, Tag, Loading } from '@carbon/react';
import './BillingDashboard.scss';

interface Metrics {
  active_subscriptions: number;
  mrr: number;
  new_this_month: number;
  cancelled_this_month: number;
  churn_rate: number;
}

// Module-level state to persist across component remounts
let metricsCache: Metrics | null = null;
let isFetching = false;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // Cache for 1 minute

let mountCounter = 0;

const BillingDashboardComponent: React.FC = () => {
  const componentId = React.useRef(++mountCounter);
  const [metrics, setMetrics] = useState<Metrics | null>(metricsCache);
  const [loading, setLoading] = useState(!metricsCache);

  // Track mount/unmount lifecycle
  React.useEffect(() => {
    console.log(`[BillingDashboard #${componentId.current}] MOUNTED`);
    return () => {
      console.log(`[BillingDashboard #${componentId.current}] UNMOUNTED`);
    };
  }, []);

  const fetchMetrics = useCallback(async () => {
    console.log(`[BillingDashboard #${componentId.current}] fetchMetrics called`);
    const now = Date.now();

    // Use cached data if available and fresh
    if (metricsCache && now - lastFetchTime < CACHE_DURATION) {
      console.log('[BillingDashboard] Using cached metrics');
      setMetrics(metricsCache);
      setLoading(false);
      return;
    }

    // If another instance is fetching, wait for it and use cached result
    if (isFetching) {
      console.log('[BillingDashboard] Fetch already in progress, waiting...');
      // Wait for the fetch to complete (max 5 seconds)
      const startWait = Date.now();
      while (isFetching && Date.now() - startWait < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Use the cached result if available
      if (metricsCache) {
        console.log('[BillingDashboard] Using metrics from concurrent fetch');
        setMetrics(metricsCache);
        setLoading(false);
        return;
      }
    }

    isFetching = true;
    try {
      // Check multiple storage locations for token (matching App.tsx auth pattern)
      const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('sigma08_jwt_token');
      const localToken = localStorage.getItem('access_token') || localStorage.getItem('sigma08_jwt_token');
      const token = sessionToken || localToken;

      console.log('[BillingDashboard] Token check:', {
        hasSessionToken: !!sessionToken,
        hasLocalToken: !!localToken,
        hasToken: !!token,
      });

      if (!token) {
        console.error('[BillingDashboard] No authentication token found');
        setLoading(false);
        return;
      }

      console.log('[BillingDashboard] Fetching metrics from /api/v1/admin/billing/metrics');
      const response = await fetch('/api/v1/admin/billing/metrics', {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('[BillingDashboard] API response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[BillingDashboard] API error:', errorText);
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[BillingDashboard] Metrics received:', data);
      metricsCache = data;
      lastFetchTime = Date.now();
      setMetrics(data);
    } catch (error) {
      console.error('[BillingDashboard] Error fetching metrics:', error);
      const fallbackMetrics = {
        active_subscriptions: 0,
        mrr: 0,
        new_this_month: 0,
        cancelled_this_month: 0,
        churn_rate: 0,
      };
      metricsCache = fallbackMetrics;
      lastFetchTime = Date.now();
      setMetrics(fallbackMetrics);
    } finally {
      console.log('[BillingDashboard] Setting loading to false');
      setLoading(false);
      isFetching = false;
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading) {
    return <Loading description="Loading metrics..." />;
  }

  return (
    <div className="admin-billing-dashboard">
      <h1 className="cds--type-heading-05">Subscription & Billing Management</h1>

      {metrics && (
        <div className="metrics-grid">
          <Tile>
            <h3 className="cds--type-heading-02">Active Subscriptions</h3>
            <p className="metric-value">{metrics.active_subscriptions}</p>
          </Tile>
          <Tile>
            <h3 className="cds--type-heading-02">MRR</h3>
            <p className="metric-value">${metrics.mrr.toFixed(2)}</p>
          </Tile>
          <Tile>
            <h3 className="cds--type-heading-02">New This Month</h3>
            <p className="metric-value">{metrics.new_this_month}</p>
            <Tag type="green">+{metrics.new_this_month}</Tag>
          </Tile>
          <Tile>
            <h3 className="cds--type-heading-02">Cancelled This Month</h3>
            <p className="metric-value">{metrics.cancelled_this_month}</p>
            {metrics.cancelled_this_month > 0 && <Tag type="red">-{metrics.cancelled_this_month}</Tag>}
          </Tile>
          <Tile>
            <h3 className="cds--type-heading-02">Churn Rate</h3>
            <p className="metric-value">{metrics.churn_rate.toFixed(2)}%</p>
            {metrics.churn_rate > 0 && <Tag type="red">{metrics.churn_rate.toFixed(1)}%</Tag>}
          </Tile>
        </div>
      )}

      <Tile className="customer-search-tile">
        <h3 className="cds--type-heading-02">Customer Search</h3>
        <Search labelText="Search customers" placeholder="Search by email, user ID, or subscription ID" />
        <p className="search-note">Feature: Search implementation pending</p>
      </Tile>

      <Tile className="subscriptions-table-tile">
        <h3 className="cds--type-heading-02">Recent Subscriptions</h3>
        <p>Feature: DataTable implementation pending</p>
        {/* TODO: DataTable with subscription list, actions (refund, cancel), filters */}
      </Tile>
    </div>
  );
};

// Wrap in React.memo to prevent unnecessary remounts when parent (HeaderContainer) re-renders
// This fixes the 10x remount issue caused by HeaderContainer's render prop pattern
export const BillingDashboard = React.memo(BillingDashboardComponent);

export default BillingDashboard;
